// Create Account screen - Link anonymous account to email/password
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Card } from '../components';
import { linkAnonymousToEmail } from '../services/authService';

const CreateAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const competitionId = location.state?.competitionId;
  const returnTo = location.state?.returnTo;
  const requireAccount = location.state?.requireAccount;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await linkAnonymousToEmail(email, password);
      setSuccess(true);
    } catch (err) {
      console.error('Error creating account:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (returnTo) {
      navigate(returnTo);
    } else if (competitionId) {
      navigate(`/competition/${competitionId}`);
    } else {
      navigate('/');
    }
  };

  const handleSkip = () => {
    // If account is required (for creating competition), don't allow skip
    if (requireAccount) {
      navigate('/');
      return;
    }

    if (competitionId) {
      navigate(`/competition/${competitionId}`);
    } else {
      navigate('/');
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full">
          <Card>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Account Created!</h2>
              <p className="text-gray-600 mb-4">
                We've sent a verification email to <strong>{email}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Please check your email and click the verification link. You can continue using the app while waiting.
              </p>
              <Button onClick={handleContinue} fullWidth>
                {requireAccount ? 'Continue to Create Competition' : 'Continue to Competition'}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create Your Account</h1>
          <p className="mt-2 text-gray-600">
            {requireAccount
              ? 'Account required to create competitions'
              : 'Secure your data across all devices'}
          </p>
        </div>

        <Card>
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Why create an account?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Access competitions from any device</li>
                <li>• Recover your data if you lose your device</li>
                <li>• Get notified about competition updates</li>
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              required
            />

            {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            {!requireAccount && (
              <button
                type="button"
                onClick={handleSkip}
                className="w-full mt-3 text-sm text-gray-600 hover:text-gray-800"
              >
                Skip for now
              </button>
            )}

            {requireAccount && (
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full mt-3 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            )}
          </form>
        </Card>

        <p className="mt-4 text-center text-xs text-gray-500">
          By creating an account, you agree to receive competition notifications via email
        </p>
      </div>
    </div>
  );
};

export default CreateAccount;
